<template>
    <a-modal
        v-model:open="visible"
        :title="title"
        :width="width"
        :confirm-loading="confirmLoading"
        :footer="footer"
        :mask-closable="maskClosable"
        :keyboard="keyboard"
        ok-text="確定"
        cancel-text="取消"
        @ok="handleOk"
        @cancel="handleCancel"
        class="custom-modal"
    >
        <slot></slot>

        <template v-if="footer === null" #footer>
            <slot name="footer"></slot>
        </template>
    </a-modal>
</template>

<script setup>
import { computed } from 'vue';

// Props
const props = defineProps({
    open: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: ''
    },
    width: {
        type: [String, Number],
        default: '600px'
    },
    confirmLoading: {
        type: Boolean,
        default: false
    },
    footer: {
        type: [Object, null],
        default: undefined
    },
    maskClosable: {
        type: Boolean,
        default: true
    },
    keyboard: {
        type: Boolean,
        default: true
    }
});

// Emits
const emit = defineEmits(['update:open', 'ok', 'cancel']);

// Computed
const visible = computed({
    get: () => props.open,
    set: (value) => emit('update:open', value)
});

// Methods
const handleOk = () => {
    emit('ok');
};

const handleCancel = () => {
    emit('cancel');
};
</script>

<style scoped>
/* 蒸氣波淺色版 Modal 樣式 */
.custom-modal :deep(.ant-modal-content),
.custom-modal :deep(.ant-modal-content .ant-modal-content),
.custom-modal :deep(.ant-modal-content .ant-modal-content .ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 2px solid transparent !important;
    border-radius: 16px !important;
    box-shadow:
        0 0 30px rgba(255, 158, 207, 0.3),
        0 0 60px rgba(42, 74, 122, 0.2),
        inset 0 0 20px rgba(168, 255, 246, 0.1) !important;
    backdrop-filter: blur(10px) !important;
}

/* 強制覆蓋所有可能的背景 */
.custom-modal :deep(.ant-modal-content *),
.custom-modal :deep(.ant-modal-content * *) {
    background-color: transparent !important;
}

.custom-modal :deep(.ant-modal-header) {
    background: transparent !important;
    border-bottom: 1px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 16px 16px 0 0 !important;
    padding: 20px 24px !important;
}

.custom-modal :deep(.ant-modal-title) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 18px !important;
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.3) !important;
}

.custom-modal :deep(.ant-modal-close) {
    color: rgba(168, 255, 246, 0.8) !important;
    transition: all 0.3s ease !important;
}

.custom-modal :deep(.ant-modal-close:hover) {
    color: #ffa87a !important;
    text-shadow: 0 0 10px rgba(255, 168, 122, 0.6) !important;
}

.custom-modal :deep(.ant-modal-body) {
    background: transparent !important;
    padding: 24px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    margin-top: 8px !important;
    max-height: min(75vh, 720px);
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.custom-modal :deep(.ant-modal-footer) {
    background: transparent !important;
    border-top: 1px solid rgba(168, 255, 246, 0.3) !important;
    border-radius: 0 0 16px 16px !important;
    padding: 24px 24px 20px 24px !important;
    margin-top: 0 !important;
}

/* Modal 遮罩層 */
.custom-modal :deep(.ant-modal-mask) {
    background: rgba(0, 0, 0, 0.8) !important;
    backdrop-filter: blur(5px) !important;
}

/* 動畫效果 */
.custom-modal :deep(.ant-modal) {
    animation: modalFadeIn 0.3s ease-out !important;
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: scale(0.9) translateY(-20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

/* 響應式設計 */
@media (max-width: 768px) {
    .custom-modal :deep(.ant-modal-content) {
        margin: 16px !important;
        width: calc(100% - 32px) !important;
    }

    .custom-modal :deep(.ant-modal-body) {
        padding: 16px !important;
    }

    .custom-modal :deep(.ant-modal-header) {
        padding: 16px !important;
    }

    .custom-modal :deep(.ant-modal-footer) {
        padding: 12px 16px !important;
    }
}
</style>
